/**
 * CTO Directive #103 — Living Presence voice client.
 *
 * This is the browser organ that lets ME *listen*, *think* and
 * *speak* as one continuous loop:
 *
 *     idle/listening  → wake word heard
 *         → waking    → capture next utterance with VAD
 *             → thinking (POST /api/me/voice/utterance)
 *                 → speaking (TTS each fragment)
 *                     → if user speaks: POST /interrupt + cancel
 *                     → else POST /done → back to listening
 *
 * Implementation uses the standard Web Speech API
 * (SpeechRecognition + SpeechSynthesis). Future sprints replace this
 * with Whisper / OpenAI Realtime / ElevenLabs via the same shape.
 *
 * The client is silent — there is no UI here. The orb watches its
 * ``onStateChange`` callbacks and changes palette accordingly. The
 * user never sees a "Recording…" badge; they just talk.
 */

import { api } from "@/lib/api";
import { playSpokenAudio, primeAudioOnFirstGesture } from "@/lib/audioChain";

const SR = typeof window !== "undefined"
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

const SILENCE_MS = 1200;
// The wake phrase must always carry the "me" marker so a bare
// "merhaba" greeting to another human does not falsely wake ME.
const WAKE_REGEX = /\b(?:merhaba|hey|hi|hello)\s+me\b/i;

export function isVoiceSupported() {
  return Boolean(SR) && typeof window !== "undefined"
    && Boolean(window.speechSynthesis);
}

export function createVoiceClient({
  language = "tr-TR",
  onStateChange = () => {},
  onAssistantText = () => {},
  onUserText = () => {},
  onAudioPhase = () => {},
} = {}) {
  if (!isVoiceSupported()) return { start: () => false, stop: () => {} };

  // CTO Directive #120.1 — arm the autoplay unlock before the first
  // /speak call. Without this the browser silently refuses playback
  // and ME would falsely think it is being heard.
  primeAudioOnFirstGesture();

  const recognition = new SR();
  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.lang = language;

  let state = "idle";              // idle | listening | waking | thinking | speaking
  let listening = false;            // recognition currently running?
  let capturingUtterance = false;   // we are after a wake word
  let captureBuf = "";
  let silenceTimer = null;
  let utteranceAbort = null;

  function setState(next) {
    if (state === next) return;
    state = next;
    onStateChange(next);
  }

  async function _sayViaBrowser(text) {
    // Last-resort fallback — used only if the backend returned no
    // audio blob. We still fire ``failed`` with a diagnostic reason
    // so the Living Conversation Stream carries an honest trace.
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    return new Promise((resolve) => {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = language;
      u.rate = 1.02;
      u.pitch = 1.0;
      u.onend = resolve;
      u.onerror = resolve;
      window.speechSynthesis.speak(u);
    });
  }

  async function _sayViaBackendThenPlay(text) {
    // First ask the backend for a real audio blob.
    let payload = null;
    try {
      const r = await api.post("/me/voice/speak", {
        text, language: language.split("-")[0] || "tr",
      });
      payload = r.data?.payload || {};
    } catch {
      payload = {};
    }
    onAssistantText(text);
    const audioB64 = payload?.audio_base64;
    if (!audioB64) {
      // Cloud voice unavailable — browser fallback with an honest
      // playback trace on the stream.
      const handle = playSpokenAudio({
        audioBase64: null,
        onPhase: onAudioPhase,
      });
      const state = await handle.done;
      await _sayViaBrowser(text);
      return state;
    }
    const handle = playSpokenAudio({
      audioBase64: audioB64,
      mimeType: payload.mime_type || "audio/mpeg",
      onPhase: onAudioPhase,
    });
    return handle.done;
  }

  async function _say(text) {
    if (!text) return;
    onAssistantText(text);
    // The full audio chain — the backend produces the sound, the
    // browser plays it, and the Living Conversation Stream carries
    // every phase so ME can honestly report success or failure.
    return _sayViaBackendThenPlay(text);
  }

  async function _think(transcript) {
    setState("thinking");
    try {
      const r = await api.post("/me/voice/utterance", {
        transcript, language: language.split("-")[0] || "tr",
      });
      const fragments = Array.isArray(r.data?.fragments) ? r.data.fragments : [];
      setState("speaking");
      for (const f of fragments) {
        if (utteranceAbort?.aborted) break;
        await _say(f.text);
      }
    } catch {
      // Silent — ME stays alive even if the network hiccups.
    } finally {
      try { await api.post("/me/voice/done"); } catch { /* silent */ }
      setState("listening");
    }
  }

  function _resetCapture() {
    capturingUtterance = false;
    captureBuf = "";
    if (silenceTimer) { clearTimeout(silenceTimer); silenceTimer = null; }
  }

  function _commit() {
    const transcript = captureBuf.trim();
    _resetCapture();
    if (!transcript) { setState("listening"); return; }
    onUserText(transcript);
    utteranceAbort = { aborted: false };
    _think(transcript);
  }

  function _handleInterim(text) {
    // While ME is speaking, any non-trivial user utterance counts as
    // an interruption — cancel TTS, mark on the server, capture.
    if (state === "speaking" && text.trim().length > 1) {
      try { window.speechSynthesis?.cancel(); } catch { /* silent */ }
      api.post("/me/voice/interrupt", { reason: "user_spoke" })
        .catch(() => {});
      utteranceAbort && (utteranceAbort.aborted = true);
      capturingUtterance = true;
      captureBuf = text;
      setState("waking");
      return;
    }
    if (capturingUtterance) {
      captureBuf = text;
      if (silenceTimer) clearTimeout(silenceTimer);
      silenceTimer = setTimeout(_commit, SILENCE_MS);
      return;
    }
    // Not yet capturing — look for the wake word.
    if (WAKE_REGEX.test(text)) {
      const phrase = (text.match(WAKE_REGEX) || [""])[0];
      const after = text.split(WAKE_REGEX).slice(2).join(" ").trim();
      api.post("/me/voice/wake", { phrase, language: language.split("-")[0] })
        .catch(() => {});
      capturingUtterance = true;
      captureBuf = after;
      setState("waking");
      if (silenceTimer) clearTimeout(silenceTimer);
      silenceTimer = setTimeout(_commit, SILENCE_MS);
    }
  }

  recognition.onresult = (e) => {
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const r = e.results[i];
      const text = r[0]?.transcript || "";
      _handleInterim(text);
    }
  };
  recognition.onerror = (e) => {
    // ``no-speech`` happens constantly while idle — ignore it.
    if (e?.error && e.error !== "no-speech" && e.error !== "aborted") {
      // Restart after non-fatal errors so listening stays continuous.
      setTimeout(() => {
        if (listening) {
          try { recognition.start(); } catch { /* silent */ }
        }
      }, 500);
    }
  };
  recognition.onend = () => {
    // Chromium auto-stops continuous recognition after ~60s — restart
    // so ME never falls asleep.
    if (listening) {
      try { recognition.start(); } catch { /* silent */ }
    }
  };

  function start() {
    if (listening) return true;
    try {
      recognition.start();
      listening = true;
      setState("listening");
      return true;
    } catch {
      listening = false;
      return false;
    }
  }

  function stop() {
    listening = false;
    _resetCapture();
    try { recognition.stop(); } catch { /* silent */ }
    try { window.speechSynthesis?.cancel(); } catch { /* silent */ }
    setState("idle");
  }

  return { start, stop, isSupported: true };
}
